// WebRTC utility functions for live streaming
import { getApiUrl } from '../config/api';

// ==== Create PeerConnection ====
export const createPeerConnection = (onIceCandidate, onTrack) => {
  const configuration = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
    ],
  };

  const pc = new RTCPeerConnection(configuration);

  // ICE events
  pc.onicecandidate = (e) => {
    if (e.candidate && onIceCandidate) onIceCandidate(e.candidate);
  };

  // Remote tracks (for viewers)
  if (onTrack) {
    pc.ontrack = (event) => {
      if (event.streams?.length) onTrack(event.streams[0]);
    };
  }

  // Optional debugging hooks
  pc.onconnectionstatechange = () => {
    console.log(`PeerConnection state: ${pc.connectionState}`);
  };
  pc.oniceconnectionstatechange = () => {
    console.log(`ICE state: ${pc.iceConnectionState}`);
  };
  pc.onicegatheringstatechange = () => {
    console.log(`ICE gathering: ${pc.iceGatheringState}`);
  };

  return pc;
};

// ==== Get User Media (Camera + Mic) ====
export const getUserMedia = async (constraints = {}, quality = 'auto') => {
  const presets = {
    high: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
    medium: { width: { ideal: 854 }, height: { ideal: 480 }, frameRate: { ideal: 24 } },
    low: { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 15 } },
    auto: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
  };
  const preset = presets[quality] || presets.auto;

  const final = {
    video: {
      facingMode: 'environment',
      ...preset,
      ...(constraints.video || {}),
    },
    audio:
      constraints.audio !== undefined
        ? constraints.audio
        : { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  };

  try {
    const stream = await navigator.mediaDevices.getUserMedia(final);

    stream.getVideoTracks().forEach((t) => {
      const s = t.getSettings();
      console.log(
        `🎥 Video: ${s.width}x${s.height} @ ${s.frameRate || '?'}fps (${t.label})`
      );
      t.onended = () => console.log('🔴 Video track ended.');
    });
    stream.getAudioTracks().forEach((t) => {
      console.log(`🎤 Audio: ${t.label}`);
      t.onended = () => console.log('🔴 Audio track ended.');
    });

    return stream;
  } catch (err) {
    console.error('Error accessing media devices:', err);
    throw err;
  }
};

// ==== Add local tracks to PeerConnection ====
export const addStreamToPeerConnection = (pc, stream, adaptive = true) => {
  if (!stream) {
    console.error('No stream to add to PeerConnection');
    return;
  }

  // For each local track
  stream.getTracks().forEach((track) => {
    const sender = pc.addTransceiver(track, { direction: 'sendonly', streams: [stream] });

    // Safari H.264 preference
    try {
      if (sender?.setCodecPreferences) {
        const codecs = RTCRtpSender.getCapabilities(track.kind)?.codecs || [];
        const h264 = codecs.filter((c) => c.mimeType === 'video/H264');
        if (h264.length > 0) sender.setCodecPreferences(h264);
      }
    } catch (err) {
      console.warn('Codec preference not supported:', err);
    }

    // Enable simulcast for adaptive bitrate (Chrome/Edge/Firefox)
    if (track.kind === 'video' && adaptive && sender?.getParameters) {
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [
          { rid: 'h', maxBitrate: 900000, scaleResolutionDownBy: 1.0 },
          { rid: 'm', maxBitrate: 300000, scaleResolutionDownBy: 2.0 },
          { rid: 'l', maxBitrate: 100000, scaleResolutionDownBy: 4.0 },
        ];
        sender
          .setParameters(params)
          .then(() => console.log('✅ Simulcast encodings applied.'))
          .catch((err) => console.warn('⚠️ Simulcast not supported:', err));
      }
    }
  });
};

// ==== Offer / Answer Handling ====
export const createOffer = async (pc) => {
  const offer = await pc.createOffer({
    offerToReceiveAudio: false,
    offerToReceiveVideo: false,
  });
  await pc.setLocalDescription(offer);
  return offer;
};

export const createAnswer = async (pc, offer) => {
  await pc.setRemoteDescription(offer);
  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);
  return answer;
};

export const handleAnswer = async (pc, answer) => {
  try {
    await pc.setRemoteDescription(answer);
  } catch (err) {
    console.error('Error applying answer:', err);
  }
};

export const addIceCandidate = async (pc, candidate) => {
  if (!candidate) return;
  try {
    await pc.addIceCandidate(candidate);
  } catch (err) {
    if (err.name === 'InvalidStateError') {
      console.warn('ICE candidate ignored (connection closed).');
    } else {
      console.error('Error adding ICE candidate:', err);
    }
  }
};

// ==== Stop local stream ====
export const stopStream = (stream) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    try {
      track.stop();
      console.log(`⏹️ Stopped ${track.kind} (${track.label})`);
    } catch {}
  });
};

// ==== Close PeerConnection ====
export const closePeerConnection = (pc) => {
  if (!pc) return;
  try {
    pc.getSenders().forEach((s) => {
      try { s.replaceTrack(null); } catch {}
    });
    pc.ontrack = pc.onicecandidate = null;
    pc.close();
    console.log('✅ PeerConnection closed.');
  } catch (err) {
    console.warn('Error closing PeerConnection:', err);
  }
};

// ==== WebSocket Signaling Client ====
export class SignalingClient {
  constructor(streamId, userId, token, role) {
    this.streamId = streamId;
    this.userId = userId;
    this.token = token;
    this.role = role; // 'broadcaster' or 'viewer'
    this.ws = null;
    this.connectionId = null;
    this.onMessage = null;
    this.onError = null;
    this.onClose = null;
  }

  async connect() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const baseUrl = getApiUrl('').replace(/\/$/, '');
    const host = new URL(baseUrl).host;
    const wsUrl = `${protocol}//${host}/ws/signaling`;

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected:', wsUrl);
          this.join();
          resolve();
        };

        this.ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (this.onMessage) this.onMessage(msg);
          } catch (err) {
            console.error('WS message parse error:', err);
          }
        };

        this.ws.onerror = (err) => {
          console.error('WebSocket error:', err);
          if (this.onError) this.onError(err);
          reject(err);
        };

        this.ws.onclose = () => {
          console.log('WebSocket closed.');
          if (this.onClose) this.onClose();
        };
      } catch (err) {
        console.error('WebSocket connect error:', err);
        reject(err);
      }
    });
  }

  join() {
    const joinMessage = {
      type: 'join',
      streamId: this.streamId,
      userId: this.userId,
      token: this.token,
      userType: this.role,
    };
    console.log(`[${this.role}] Sending join message:`, joinMessage);
    this.send(joinMessage);
  }

  send(msg) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  sendOffer(offer, targetConnectionId) {
    this.send({
      type: 'offer',
      streamId: this.streamId,
      userId: this.userId,
      token: this.token,
      offer,
      targetConnectionId,
    });
  }

  sendAnswer(answer, targetConnectionId) {
    this.send({
      type: 'answer',
      streamId: this.streamId,
      userId: this.userId,
      token: this.token,
      answer,
      targetConnectionId,
    });
  }

  sendIceCandidate(candidate, targetConnectionId) {
    this.send({
      type: 'ice-candidate',
      streamId: this.streamId,
      userId: this.userId,
      token: this.token,
      candidate,
      targetConnectionId,
    });
  }

  leave() {
    this.send({
      type: 'leave',
      streamId: this.streamId,
      userId: this.userId,
      token: this.token,
    });
  }

  disconnect() {
    try {
      this.ws?.close();
      this.ws = null;
    } catch {}
  }
}
